import { Type, Transform } from 'class-transformer';
import {
  IsInt,
  IsNumber,
  IsIn,
  IsArray,
  IsString,
  IsDateString,
  IsUUID,
  IsNotEmpty,
  ValidateNested,
  IsOptional,
  IsBoolean,
} from 'class-validator';
import { injectable } from 'inversify';

import { HttpService } from '@vk-hr-tek/core/http';
import {
  FiltersResponse,
  Filter,
  FilterService,
  FilterType,
} from '@vk-hr-tek/core/filter';
import { ValidationService } from '@vk-hr-tek/core/validation';
import { UnmarshallerService } from '@vk-hr-tek/core/unmarshaller';
import { QueryService } from '@vk-hr-tek/core/query';

export type CustomIconEnum = string;

export class CustomLinkItem {
  @IsIn(['custom_link_item'])
  @IsString()
  @IsNotEmpty()
  type: 'custom_link_item';

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsNotEmpty()
  link: string;

  @IsIn([
    'globe',
    'document',
    'bookmark',
    'picture',
    'cup',
    'briefcase',
    'advertising',
    'user',
  ])
  @IsString()
  @IsNotEmpty()
  icon:
    | 'globe'
    | 'document'
    | 'bookmark'
    | 'picture'
    | 'cup'
    | 'briefcase'
    | 'advertising'
    | 'user';

  @IsBoolean()
  @IsNotEmpty()
  external_system: boolean;

  @IsBoolean()
  @IsNotEmpty()
  blank: boolean;
}

export class GetSideMenuUserSectionResponse {
  @IsString()
  @IsNotEmpty()
  name: string;

  @ValidateNested({ each: true })
  @Type(() => CustomLinkItem)
  @IsArray()
  @IsNotEmpty()
  children: CustomLinkItem[];
}

export class UserSectionSettingsItem {
  @IsIn(['user_section_item'])
  @IsString()
  @IsNotEmpty()
  type: 'user_section_item';

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsBoolean()
  @IsNotEmpty()
  switched_on: boolean;
}

export type LinkItemNickEnum = string;

export class SystemLinkItem {
  @IsIn(['link_item'])
  @IsString()
  @IsNotEmpty()
  type: 'link_item';

  @IsIn([
    'main',
    'news',
    'structure',
    'employee_personal',
    'employee_events',
    'employee_policy',
    'employee_absences',
    'employee_payslips',
    'employee_vacations',
    'employee_competencies',
    'employee_review',
    'company_events',
    'company_events_paper',
    'company_policy',
    'company_absences',
    'company_vacations',
    'company_employees',
    'company_candidates',
    'company_review',
    'company_social_news',
    'company_social_quiz',
    'company_social_categories',
    'company_social_access',
    'company_settings',
    'company_settings_event_types',
    'company_settings_templates',
    'company_settings_groups',
    'company_settings_profiles',
    'company_settings_substitutes',
    'company_settings_vacations',
    'company_settings_attorneys',
    'company_settings_attorneys_users',
    'company_settings_sign_types',
  ])
  @IsString()
  @IsNotEmpty()
  nick:
    | 'main'
    | 'news'
    | 'structure'
    | 'employee_personal'
    | 'employee_events'
    | 'employee_policy'
    | 'employee_absences'
    | 'employee_payslips'
    | 'employee_vacations'
    | 'employee_competencies'
    | 'employee_review'
    | 'company_events'
    | 'company_events_paper'
    | 'company_policy'
    | 'company_absences'
    | 'company_vacations'
    | 'company_employees'
    | 'company_candidates'
    | 'company_review'
    | 'company_social_news'
    | 'company_social_quiz'
    | 'company_social_categories'
    | 'company_social_access'
    | 'company_settings'
    | 'company_settings_event_types'
    | 'company_settings_templates'
    | 'company_settings_groups'
    | 'company_settings_profiles'
    | 'company_settings_substitutes'
    | 'company_settings_vacations'
    | 'company_settings_attorneys'
    | 'company_settings_attorneys_users'
    | 'company_settings_sign_types';
}

export class CustomDropdownItem {
  @IsIn(['custom_dropdown_item'])
  @IsString()
  @IsNotEmpty()
  type: 'custom_dropdown_item';

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn([
    'globe',
    'document',
    'bookmark',
    'picture',
    'cup',
    'briefcase',
    'advertising',
    'user',
  ])
  @IsString()
  @IsNotEmpty()
  icon:
    | 'globe'
    | 'document'
    | 'bookmark'
    | 'picture'
    | 'cup'
    | 'briefcase'
    | 'advertising'
    | 'user';

  @IsBoolean()
  @IsNotEmpty()
  external_system: boolean;

  @IsArray()
  @IsNotEmpty()
  children: (SystemLinkItem | CustomLinkItem)[];

  @IsBoolean()
  @IsNotEmpty()
  blank: boolean;
}

export type DropdownItemNickEnum = string;

export class SystemDropdownItem {
  @IsIn(['dropdown_item'])
  @IsString()
  @IsNotEmpty()
  type: 'dropdown_item';

  @IsIn([
    'dropdown_company_events',
    'dropdown_company_settings',
    'dropdown_social_services',
  ])
  @IsString()
  @IsNotEmpty()
  nick:
    | 'dropdown_company_events'
    | 'dropdown_company_settings'
    | 'dropdown_social_services';

  @IsArray()
  @IsNotEmpty()
  children: (SystemLinkItem | CustomLinkItem)[];
}

export class CustomSectionItem {
  @IsIn(['custom_section_item'])
  @IsString()
  @IsNotEmpty()
  type: 'custom_section_item';

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsBoolean()
  @IsNotEmpty()
  external_system: boolean;

  @IsArray()
  @IsNotEmpty()
  children: (
    | SystemLinkItem
    | SystemDropdownItem
    | CustomLinkItem
    | CustomDropdownItem
  )[];

  @IsBoolean()
  @IsNotEmpty()
  blank: boolean;
}

export type SectionItemNickEnum = string;

export class SystemSectionItem {
  @IsIn(['section_item'])
  @IsString()
  @IsNotEmpty()
  type: 'section_item';

  @IsIn(['company_services', 'employee_services'])
  @IsString()
  @IsNotEmpty()
  nick: 'company_services' | 'employee_services';

  @IsArray()
  @IsNotEmpty()
  children: (
    | SystemLinkItem
    | SystemDropdownItem
    | CustomLinkItem
    | CustomDropdownItem
  )[];
}

export class GetSideMenuSettingsResponse {
  @IsArray()
  @IsNotEmpty()
  menu: (
    | SystemLinkItem
    | SystemDropdownItem
    | SystemSectionItem
    | CustomLinkItem
    | CustomDropdownItem
    | CustomSectionItem
    | UserSectionSettingsItem
  )[];
}

export class UserSectionItem {
  @IsIn(['user_section_item'])
  @IsString()
  @IsNotEmpty()
  type: 'user_section_item';

  @IsString()
  @IsNotEmpty()
  name: string;

  @ValidateNested({ each: true })
  @Type(() => CustomLinkItem)
  @IsArray()
  @IsNotEmpty()
  children: CustomLinkItem[];
}

export class GetSideMenuResponse {
  @IsArray()
  @IsNotEmpty()
  menu: (
    | SystemLinkItem
    | SystemDropdownItem
    | SystemSectionItem
    | CustomLinkItem
    | CustomDropdownItem
    | CustomSectionItem
    | UserSectionItem
  )[];
}

export class UpdateSideMenuUserSectionRequest {
  @IsString()
  @IsNotEmpty()
  name: string;

  @ValidateNested({ each: true })
  @Type(() => CustomLinkItem)
  @IsArray()
  @IsNotEmpty()
  children: CustomLinkItem[];
}

export class UpdateSideMenuSettingsRequest {
  @IsArray()
  @IsNotEmpty()
  menu: (
    | SystemLinkItem
    | SystemDropdownItem
    | SystemSectionItem
    | CustomLinkItem
    | CustomDropdownItem
    | CustomSectionItem
    | UserSectionSettingsItem
  )[];
}

@injectable()
export class SideMenuService {
  constructor(
    private validator: ValidationService,
    private http: HttpService,
    private unmarshaller: UnmarshallerService,
    private query: QueryService,
  ) {}

  async updateSideMenuUserSection({
    body,
  }: {
    body: UpdateSideMenuUserSectionRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(body, UpdateSideMenuUserSectionRequest),
    ]);

    let requestPath = '/side_menu/user_section';

    const result = await this.http.put(requestPath, body, { withSide: true });

    return result;
  }

  async getSideMenuUserSection() {
    let requestPath = '/side_menu/user_section';

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      GetSideMenuUserSectionResponse,
    );

    return response;
  }

  async updateSideMenuSettings({
    body,
  }: {
    body: UpdateSideMenuSettingsRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(body, UpdateSideMenuSettingsRequest),
    ]);

    let requestPath = '/side_menu/settings';

    const result = await this.http.put(requestPath, body, { withSide: true });

    return result;
  }

  async getSideMenuSettings() {
    let requestPath = '/side_menu/settings';

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      GetSideMenuSettingsResponse,
    );

    return response;
  }

  async getSideMenu() {
    let requestPath = '/side_menu';

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      GetSideMenuResponse,
    );

    return response;
  }
}
